import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Form, FormGroup, Button } from 'reactstrap';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector, destroy } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import {
  getPaymentMethodsPerSecurityProfile,
  getCreditNotes,
  clearCreditNotes,
  postPaymentV2,
} from '../../../../actions';
import { isRequired } from '../../../../utils/validators';
import {
  CREDIT_NOTE,
  CONTRACT_TYPE,
  TRANSACTION_TYPE_BUSINESS_CREDIT,
  DEFAULT_ORDER,
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
} from '../../../../config/constants';
import {
  DEFAULT_CORPORATE_VOUCHER_TYPE_OPTION,
  DEFAULT_PAYMENT_METHOD_OPTION,
  DEFAULT_SELF_SERVICE_VOUCHER_TYPE_OPTION,
  DEFAULT_VOUCHER_TYPE_OPTION,
  DEV_SELF_SERVICE_PAYMENT_METHOD_ID,
} from '../../../../config/defaults';
import VoucherTypeInputGroup from '../../../common/forms/VoucherTypeInputGroup';
import Select from '../../../common/forms/select/Select';
import { optionPropTypes } from '../../../common/forms/select/SelectPropTypes';
import FormItem from '../../../common/forms/FormItem';
import Badge from '../../../common/Badge';
import { tzNormalizeDate } from '../../../../utils/date';
import {
  clearDiscountCodeSubCategories,
  getDiscountCodeSubCategoriesByDiscountId,
} from '../../../../actions/sales/DiscountCodeSubCategory';
import { TIMESTAMP_FORMAT } from '../../../../config/locale';
import {
  clearFreeOfChargeCategory,
  getFreeOfChargeCategory,
} from '../../../../actions/sales/FreeOfChargeCategory';
import MultiPaymentMethodInputGroup, {
  isPinpadLocked,
} from '../../../common/forms/MultiPaymentMethodInputGroup';
import {
  buildTransactionList,
  flagUsedPinpadTransactions,
  summarizeSplit,
} from '../../../../utils/units/paymentSplit';

const selector = formValueSelector('PaymentForm');

export const PaymentForm = ({
  change,
  clients,
  submitting,
  totalPrice,
  seatsNumber,
  creditNotes,
  handleSubmit,
  paymentMethods,
  contingencyData,
  discountCodeId,
  discountCodeSubCategories,
  subCategoryAuthorizers,
  activeSaleSession,
  dispatchPostPayment,
  dispatchGetCreditNotes,
  dispatchClearCreditNotes,
  dispatchGetDiscountCodeSubCategoriesByDiscountId,
  dispatchClearDiscountCodeSubCategories,
  dispatchGetPaymentMethodsPerSecurityProfile,
  dispatchDestroyPaymentForm,
  dispatchClearFreeOfChargeCategory,
  disabledButton,
  pinpadLocked,
}) => {
  const [hasCreditNoteCode, setHasCreditNoteCode] = useState(false);
  const [showDiscountSubCategory, setShowDiscountSubCategory] = useState(false);

  useLayoutEffect(() => {
    dispatchGetPaymentMethodsPerSecurityProfile();
    if (clients.length === 1) {
      const query = [
        `customerId:${clients[0].value}`,
        'isValid:true',
        'previousTicketId:!Ø',
      ];
      dispatchGetCreditNotes({ query });
    }

    if (discountCodeId) {
      dispatchGetDiscountCodeSubCategoriesByDiscountId(discountCodeId, {
        page: 0,
        size: 1000,
        sort: 'id,desc',
        order: DEFAULT_ORDER,
      });
    }

    return () => {
      dispatchClearCreditNotes();
      dispatchClearDiscountCodeSubCategories();
      dispatchDestroyPaymentForm();
      dispatchClearFreeOfChargeCategory();
    };
  }, []);

  useEffect(() => {
    const hasOneSubcat =
      Array.isArray(discountCodeSubCategories) &&
      discountCodeSubCategories.length === 1;
    const hasOneAuthorizer =
      Array.isArray(subCategoryAuthorizers) &&
      subCategoryAuthorizers.length === 1;

    if (hasOneSubcat) {
      const onlySubcat = discountCodeSubCategories[0];
      change('discountCodeSubCategory', onlySubcat);

      if (hasOneAuthorizer) {
        const onlyAuth = subCategoryAuthorizers[0];
        change('discountCodeSubCategoryAuthorizer', onlyAuth);
        setShowDiscountSubCategory(false);
        return;
      }

      setShowDiscountSubCategory(true);
    } else {
      setShowDiscountSubCategory(
        Array.isArray(discountCodeSubCategories) &&
          discountCodeSubCategories.length >= 1,
      );
    }
  }, [discountCodeSubCategories, subCategoryAuthorizers, change]);

  useEffect(() => {
    setHasCreditNoteCode(creditNotes.length > 0);
  }, [creditNotes]);

  const contingencyDocumentList = (contingencyDataValue) =>
    Object.keys(contingencyDataValue).map((ticketId) => {
      const contingencyDetails = contingencyDataValue[ticketId];

      const contigencyDate =
        contingencyDetails.contingencyAssignmentDocumentDate
          ? tzNormalizeDate({
              date: contingencyDetails.contingencyAssignmentDocumentDate,
            })
          : tzNormalizeDate();
      return {
        documentSerie: contingencyDetails.contingencySeries,
        documentCode: parseInt(contingencyDetails.contingencyCode, 10),
        ticketId: parseInt(contingencyDetails.ticketId, 10),
        contingencyMotive: contingencyDetails.contingencyMotive,
        contingencyAssignmentDocumentDate: contigencyDate,
      };
    });

  const validateContingencyData = (contingencyDataValue, contingencyFlag) => {
    if (!contingencyFlag) {
      return true;
    }

    if (!contingencyData || contingencyData.length === 0) {
      return false;
    }

    return contingencyData.every((data) => {
      if (
        !data.contingencySeries ||
        !data.contingencyCode ||
        !data.contingencyMotive ||
        !data.contingencyAssignmentDocumentDate
      ) {
        return false;
      }

      const isValidSeries = /^[0-9]{4}$/.test(data.contingencySeries);

      const isValidCode = /^[0-9]+$/.test(data.contingencyCode);

      return isValidSeries && isValidCode;
    });
  };

  const handlePaymentSubmit = (formValues) => {
    const currentContingencyDocumentList =
      contingencyDocumentList(contingencyData);

    const isContingencySalesSession =
      activeSaleSession.get('workstation').contingency;

    if (
      !validateContingencyData(
        currentContingencyDocumentList,
        isContingencySalesSession,
      )
    ) {
      toastr.error(
        'Error',
        'Por favor, complete todos los campos de contingencia. Asegúrese de que la serie tenga 4 dígitos y el código solo contenga números.',
      );
      return;
    }

    const summary = summarizeSplit(
      formValues.transactionList || [],
      totalPrice,
    );

    if (summary.hasDuplicateMethods) {
      toastr.error(
        'Error',
        'No puede repetir el mismo método de pago en dos filas',
      );
      return;
    }

    if (!summary.isValid) {
      toastr.error(
        'Error',
        summary.isPending
          ? 'Los montos no cubren el total a pagar'
          : 'Los montos superan el total a pagar',
      );
      return;
    }

    const { transactions, freeOfCharge } = buildTransactionList(
      formValues.transactionList || [],
      {
        totalPrice,
        customerId: formValues.customer.value,
        voucherTypeId: formValues.voucherType.value,
        businessId: formValues.business ? formValues.business.value : null,
      },
    );

    transactions.forEach((t) => {
      if (t.depositDate) {
        // eslint-disable-next-line no-param-reassign
        t.depositDate = tzNormalizeDate({
          date: t.depositDate,
          format: TIMESTAMP_FORMAT,
        });
      }
    });

    let discountCodeSubCategoryId = null;
    if (
      Array.isArray(discountCodeSubCategories) &&
      discountCodeSubCategories.length === 1
    ) {
      discountCodeSubCategoryId = discountCodeSubCategories[0].value;
    } else if (formValues.discountCodeSubCategory) {
      discountCodeSubCategoryId = formValues.discountCodeSubCategory.value;
    }

    let discountCodeAuthorizerId = null;
    if (formValues.discountCodeSubCategoryAuthorizer) {
      discountCodeAuthorizerId =
        formValues.discountCodeSubCategoryAuthorizer.value;
    } else if (
      Array.isArray(subCategoryAuthorizers) &&
      subCategoryAuthorizers.length === 1
    ) {
      discountCodeAuthorizerId = subCategoryAuthorizers[0].value;
    }

    const payReservationRequest = {
      transactionList: transactions,
      contingencyDocumentCorrelationList: currentContingencyDocumentList,
      discountCodeSubCategoryId,
      discountCodeAuthorizerId,
      freeOfChargeCategoryId: freeOfCharge.categoryId,
      freeOfChargeSubCategoryId: freeOfCharge.subCategoryId,
      freeOfChargeUserId: freeOfCharge.userId,
      pinPadTransactions: flagUsedPinpadTransactions(
        formValues.pinPadTransactions || [],
        formValues.transactionList || [],
      ),
    };

    dispatchPostPayment({
      payReservationRequest,
      customerOrderId: formValues.customerOrderId,
      businessTaxId: formValues.businessTaxId,
      businessName: formValues.businessName,
      addressSummary: formValues.addressSummary,
    });
  };

  const creditNoteResource = hasCreditNoteCode && (
    <Badge color="success" text="Este cliente tiene una nota de crédito" />
  );

  return (
    <div>
      <br />
      <h2>Pago</h2>
      <Form onSubmit={handleSubmit(handlePaymentSubmit)}>
        <FormGroup row>
          <FormItem label="Cliente" required>
            <Field
              name="customer"
              component={Select}
              options={clients}
              validate={[isRequired]}
            />
            {creditNoteResource}
          </FormItem>
        </FormGroup>
        {showDiscountSubCategory && (
          <>
            <FormGroup row>
              <FormItem label="Descuento SubCategoría" required>
                <Field
                  name="discountCodeSubCategory"
                  component={Select}
                  options={discountCodeSubCategories}
                  validate={[isRequired]}
                  onChange={() =>
                    change('discountCodeSubCategoryAuthorizer', null)
                  }
                />
              </FormItem>
            </FormGroup>
            <FormGroup row>
              <FormItem label="Autorizador" required>
                <Field
                  name="discountCodeSubCategoryAuthorizer"
                  component={Select}
                  options={subCategoryAuthorizers}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
          </>
        )}
        <VoucherTypeInputGroup
          voucherTypeFieldName="voucherType"
          businessFieldName="business"
          businessTaxIdFieldName="businessTaxId"
          businessNameFieldName="businessName"
          addressSummaryFieldName="addressSummary"
          change={change}
        />
        <MultiPaymentMethodInputGroup
          formName="PaymentForm"
          totalPrice={totalPrice}
          recomendedPaymentMethod={paymentMethods}
          transactionCodesNotIncluded={
            seatsNumber > 1
              ? [
                  TRANSACTION_TYPE_CREDIT_NOTE,
                  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
                ]
              : [TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL]
          }
        />
        <div className="FormButtonGroup">
          <Button
            type="submit"
            disabled={submitting || disabledButton || pinpadLocked}
            color="primary"
            size="lg"
          >
            Pagar <i className="fa fa-arrow-circle-right" />
          </Button>
        </div>
      </Form>
    </div>
  );
};

PaymentForm.propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  paymentMethods: Select.propTypes.options.isRequired,
  clients: Select.propTypes.options.isRequired,
  dispatchGetPaymentMethodsPerSecurityProfile: PropTypes.func.isRequired,
  dispatchGetCreditNotes: PropTypes.func.isRequired,
  dispatchClearCreditNotes: PropTypes.func.isRequired,
  dispatchGetDiscountCodeSubCategoriesByDiscountId: PropTypes.func.isRequired,
  dispatchClearDiscountCodeSubCategories: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  totalPrice: PropTypes.number.isRequired,
  dispatchPostPayment: PropTypes.func.isRequired,
  seatsNumber: PropTypes.number.isRequired,
  paymentMethodValue: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    transactionCode: PropTypes.string,
  }),
  initialValues: PropTypes.shape({
    customer: optionPropTypes,
  }),
  activeSaleSession: PropTypes.instanceOf(Immutable.Map).isRequired,
  contingencyData: PropTypes.arrayOf(
    PropTypes.shape({
      documentSerie: PropTypes.string,
      documentCode: PropTypes.number,
      ticketId: PropTypes.number,
      contingencyMotive: PropTypes.string,
    }),
  ),
  discountCodeId: PropTypes.number,
  // selectedCreditNotes: PropTypes.instanceOf(Array),
  creditNotes: PropTypes.instanceOf(Array),
  discountCodeSubCategories: PropTypes.instanceOf(Array),
  subCategoryAuthorizers: PropTypes.instanceOf(Array),
  dispatchDestroyPaymentForm: PropTypes.func.isRequired,
  // dispatchGetFreeOfChargeCategory: PropTypes.func.isRequired,
  // freeOfChargeSubCategories: PropTypes.instanceOf(Array),
  // freeOfChargeUsers: PropTypes.instanceOf(Array),
  dispatchClearFreeOfChargeCategory: PropTypes.func.isRequired,
  disabledButton: PropTypes.bool,
  pinpadLocked: PropTypes.bool.isRequired,
};

PaymentForm.defaultProps = {
  // selectedCreditNotes: [],
  creditNotes: [],
  paymentMethodValue: null,
  initialValues: null,
  contingencyData: [],
  discountCodeId: null,
  discountCodeSubCategories: [],
  subCategoryAuthorizers: [],
  // freeOfChargeSubCategories: [],
  // freeOfChargeUsers: [],
  disabledButton: false,
};

const mapStateToProps = (state) => {
  const clients = state.BookingUnit.Booking.getIn([
    'payment',
    'customerOrder',
    'passengers',
  ]).map((passenger) => ({
    value: passenger.id,
    label: passenger.fullName,
  }));

  const paymentMethods = state.AccountingUnit.PaymentMethod.getIn([
    'all',
    'content',
    'content',
  ]).map((paymentMethod) => ({
    value: paymentMethod.id,
    label: paymentMethod.name,
    transactionCode: paymentMethod.transactionCode,
  }));

  let newPaymentMethods = [];

  let defaultPaymentMethod = DEFAULT_PAYMENT_METHOD_OPTION;

  let defaultVoucherType = DEFAULT_VOUCHER_TYPE_OPTION;

  let defaultBusiness = null;

  // Checking self service sale
  const { salesSessionUserId } = state.authentication.get('user');

  const activeSaleSession = state.SalesUnit.SalesSession.getIn([
    'active',
    'content',
  ]);

  if (!salesSessionUserId) {
    // Checking corporate sale

    const { contractType, company } = activeSaleSession.get('agency');

    if (contractType === CONTRACT_TYPE.CORPORATE.value) {
      defaultPaymentMethod = null;

      defaultVoucherType = DEFAULT_CORPORATE_VOUCHER_TYPE_OPTION;

      paymentMethods.forEach((method) => {
        if (
          method.transactionCode === CREDIT_NOTE ||
          method.transactionCode === TRANSACTION_TYPE_BUSINESS_CREDIT
        ) {
          newPaymentMethods.push(method);
        }
      });

      if (company) {
        defaultBusiness = {
          value: company.business.businessTaxId,
          label: company.business.name,
        };
      }
    } else {
      newPaymentMethods = paymentMethods;
    }
  } else {
    defaultPaymentMethod = DEV_SELF_SERVICE_PAYMENT_METHOD_ID;

    newPaymentMethods.push(defaultPaymentMethod);

    defaultVoucherType = DEFAULT_SELF_SERVICE_VOUCHER_TYPE_OPTION;
  }

  const businesses = state.UserUnit.Business.getIn([
    'all',
    'content',
    'content',
  ]).map(({ id, businessTaxId, name }) => ({
    value: id,
    label: `${businessTaxId} - ${name}`,
  }));

  const customerOrderId = state.BookingUnit.Booking.getIn([
    'payment',
    'customerOrder',
    'id',
  ]);

  const initialValues = {
    customerOrderId,
    // by default select the first client as the one who pays
    customer: clients[0],
    voucherType: defaultVoucherType,
    paymentMethod: null,
    business: defaultBusiness,
  };

  const selectedCreditNotes = selector(state, 'creditNotes');
  const paymentMethodValue = selector(state, 'paymentMethod');
  const creditNotes = state.SalesUnit.CreditNote.getIn([
    'all',
    'content',
    'content',
  ]);

  const rawSubcats = state.SalesUnit.DiscountCodeSubCategory.getIn([
    'all',
    'content',
    'content',
  ]);

  const discountCodeSubCategories = rawSubcats.map((subCategory) => ({
    value: subCategory.id,
    label: subCategory.name,
  }));

  const selectedSubcatField = selector(state, 'discountCodeSubCategory');
  const chosenSubcatId =
    (selectedSubcatField && selectedSubcatField.value) ||
    (discountCodeSubCategories.length === 1
      ? discountCodeSubCategories[0].value
      : null);

  let subCategoryAuthorizers = [];
  if (chosenSubcatId) {
    const sc = rawSubcats.find((s) => s.id === chosenSubcatId);
    if (sc && Array.isArray(sc.discountCodeSubCategoryAuthorizerList)) {
      subCategoryAuthorizers = sc.discountCodeSubCategoryAuthorizerList
        .filter((a) => a.active !== false)
        .map((a) => {
          const auth = a.discountCodeAuthorizer;
          const label =
            auth.customer.fullName ||
            `Autorizador ${a.discountCodeAuthorizerId}`;
          return {
            value: a.discountCodeAuthorizerId,
            label,
          };
        });
    }
  }

  const categoryState = state.SalesUnit.FreeOfChargeCategory.getIn([
    'current',
    'content',
  ]);

  let freeOfChargeSubCategories = [];
  let freeOfChargeUsers = [];

  const category =
    categoryState && categoryState.toJS ? categoryState.toJS() : categoryState;

  if (category) {
    const subcats = category.freeOfChargeSubCategoryList || [];

    freeOfChargeSubCategories = subcats.map((sc) => ({
      value: sc.id,
      label: sc.name,
    }));

    const selectedSubcat = selector(state, 'freeOfChargeSubCategory');

    if (selectedSubcat) {
      const found = subcats.find((sc) => sc.id === selectedSubcat.value);

      if (found) {
        freeOfChargeUsers = (found.freeOfChargeSubCategoryUserList || []).map(
          (u) => ({
            value: u.customer.id,
            label: u.customer.fullName,
          }),
        );
      }
    }
  }
  return {
    paymentMethods: newPaymentMethods,
    clients,
    businesses,
    initialValues,
    creditNotes,
    selectedCreditNotes,
    paymentMethodValue,
    activeSaleSession,
    discountCodeSubCategories,
    subCategoryAuthorizers,
    freeOfChargeSubCategories,
    freeOfChargeUsers,
    pinpadLocked: isPinpadLocked('PaymentForm')(state),
  };
};

const mapDispatchToProps = {
  dispatchGetPaymentMethodsPerSecurityProfile:
    getPaymentMethodsPerSecurityProfile,
  dispatchPostPayment: postPaymentV2,
  dispatchGetCreditNotes: getCreditNotes,
  dispatchClearCreditNotes: clearCreditNotes,
  dispatchGetDiscountCodeSubCategoriesByDiscountId:
    getDiscountCodeSubCategoriesByDiscountId,
  dispatchClearDiscountCodeSubCategories: clearDiscountCodeSubCategories,
  dispatchGetFreeOfChargeCategory: getFreeOfChargeCategory,
  dispatchClearFreeOfChargeCategory: clearFreeOfChargeCategory,

  dispatchDestroyPaymentForm: () => destroy('PaymentForm'),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'PaymentForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  })(PaymentForm),
);
